'use client';

import { c as _c } from "react/compiler-runtime";
import type { SearchedUser, SearchFactiii } from '@shared/all/domains/factiiis';
import { DeploymentLayout, type SearchedSpace } from '@shared/all/domains/spaces';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import { useQuery } from '@tanstack/react-query';
import Link from 'next/link';
import { useKeyboardNavigation } from '../../hooks/useKeyboardNavigation';
import { spaceConfig } from '../../utils/getSpaceConfig';
import { client } from '../../utils/trpc';
import Avatar from '../common/Avatar';
import Error from '../common/Error';
import ScrollableText from '../UI/ScrollableText';
type TagType = 'user' | 'space' | 'factiii';
type Props = {
  type: TagType;
  query: string;
  caretPosition: {
    top: number;
    left: number;
  } | null;
  closeHandler: () => void;
  itemClickHandler: (slug: string, id: number) => void;
  fullWidth?: boolean;
};
type ItemType = SearchedUser | SearchedSpace | SearchFactiii;
const TagSuggestionModal = props => {
  const $ = _c(59);
  let t0;
  if ($[0] !== props.query || $[1] !== props.type) {
    t0 = {
      queryKey: ["search", props.type, props.query],
      queryFn: async () => {
        if (props.type === "user") {
          return await unwrapFromResult(client.search.users.query({
            query: props.query,
            spaceIds: spaceConfig.layoutType === DeploymentLayout.Space ? [spaceConfig.space.id] : undefined
          }));
        } else {
          if (props.type === "factiii") {
            return await unwrapFromResult(client.search.factiiis.query({
              query: props.query
            }));
          } else {
            return await unwrapFromResult(client.search.spaces.query({
              query: props.query
            }));
          }
        }
      }
    };
    $[0] = props.query;
    $[1] = props.type;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const {
    isLoading,
    isError,
    data
  } = useQuery(t0);
  let t1;
  if ($[3] !== data || $[4] !== props.type) {
    const getItems = () => {
      if (props.type === "user" && data && "users" in data) {
        return data.users ?? [];
      } else {
        if (props.type === "space" && data && "spaces" in data) {
          return data.spaces ?? [];
        } else {
          if (props.type === "factiii" && data && "factiiis" in data) {
            return data.factiiis ?? [];
          }
        }
      }
      return [];
    };
    t1 = getItems();
    $[3] = data;
    $[4] = props.type;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const items = t1;
  let t2;
  if ($[6] !== props) {
    t2 = item => {
      if (props.type === "user" && "username" in item) {
        props.itemClickHandler(item.username, item.id);
      } else {
        if (props.type === "space" && "spaceSlug" in item && item.spaceSlug) {
          props.itemClickHandler(item.spaceSlug, item.id);
        } else {
          if (props.type === "factiii" && "slug" in item) {
            props.itemClickHandler(item.slug, item.id);
          }
        }
      }
    };
    $[6] = props;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const handleSelect = t2;
  let t3;
  if ($[8] !== props.query) {
    t3 = [props.query];
    $[8] = props.query;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== handleSelect || $[11] !== items || $[12] !== props.closeHandler || $[13] !== t3) {
    t4 = {
      items,
      onSelect: handleSelect,
      onClose: props.closeHandler,
      resetDeps: t3
    };
    $[10] = handleSelect;
    $[11] = items;
    $[12] = props.closeHandler;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const {
    getItemProps
  } = useKeyboardNavigation(t4);
  let t5;
  if ($[15] !== props) {
    t5 = (slug, id) => {
      props.itemClickHandler(slug, id);
    };
    $[15] = props;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const handleItemClick = t5;
  let t6;
  if ($[17] !== getItemProps || $[18] !== handleItemClick) {
    t6 = (user, index) => {
      const {
        ref,
        onMouseEnter,
        isSelected
      } = getItemProps(index);
      return <div key={user.username} ref={ref} className={`flex cursor-pointer items-center space-x-4 rounded border border-ds-border-standard p-2 ${isSelected ? "bg-background-secondary" : "hover:bg-background-light"}`} onMouseDown={e => {
        e.preventDefault();
        handleItemClick(user.username, user.id);
      }} onMouseEnter={onMouseEnter}><Avatar avatarKey={user.avatarKey} robohash={user.robohash ?? ""} isBot={user.tag === "BOT"} size={40} userId={user.id} /><div className="min-w-0">{user.name ? <><ScrollableText className="font-semibold">{user.name}</ScrollableText><ScrollableText className="text-textSecondary/70">@{user.username}</ScrollableText></> : <><ScrollableText className="font-semibold">@{user.username}</ScrollableText></>}</div></div>;
    };
    $[17] = getItemProps;
    $[18] = handleItemClick;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const renderUserItem = t6;
  let t7;
  if ($[20] !== getItemProps || $[21] !== handleItemClick) {
    t7 = (space, index_0) => {
      const {
        ref: ref_0,
        onMouseEnter: onMouseEnter_0,
        isSelected: isSelected_0
      } = getItemProps(index_0);
      return <div key={space.spaceSlug} ref={ref_0} className={`flex cursor-pointer items-center space-x-4 rounded border border-ds-border-standard p-2 ${isSelected_0 ? "bg-background-secondary" : "hover:bg-background-light"}`} onMouseDown={e_0 => {
        e_0.preventDefault();
        handleItemClick(space.spaceSlug, space.id);
      }} onMouseEnter={onMouseEnter_0}><Avatar spaceAvatarKey={space.avatarKey} size={40} /><div className="min-w-0"><ScrollableText>{space.name}</ScrollableText><ScrollableText className="text-textSecondary/70">s/{space.spaceSlug}</ScrollableText></div></div>;
    };
    $[20] = getItemProps;
    $[21] = handleItemClick;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const renderSpaceItem = t7;
  let t8;
  if ($[23] !== getItemProps || $[24] !== handleItemClick) {
    t8 = (factiii, index_1) => {
      const {
        ref: ref_1,
        onMouseEnter: onMouseEnter_1,
        isSelected: isSelected_1
      } = getItemProps(index_1);
      return <div key={factiii.id} ref={ref_1} className={`flex cursor-pointer items-center space-x-4 rounded border border-ds-border-standard p-2 ${isSelected_1 ? "bg-background-secondary" : "hover:bg-background-light"}`} onMouseDown={e_1 => {
        e_1.preventDefault();
        handleItemClick(factiii.slug, factiii.id);
      }} onMouseEnter={onMouseEnter_1}><Avatar factiiiAvatarKey={factiii.avatarKey} factiiiId={factiii.id} size={40} /><div className="min-w-0"><ScrollableText>{factiii.name}</ScrollableText><div className=""><ScrollableText className="text-textSecondary/70">f/{factiii.slug} </ScrollableText><ScrollableText>{factiii.spaceSlug ? <>in{" "}<Link href={`/s/${factiii.spaceSlug}`}>{factiii.spaceSlug}</Link></> : <>by{" "}<Link href={`/user/${factiii.username}`}>{factiii.username}</Link></>}</ScrollableText></div></div></div>;
    };
    $[23] = getItemProps;
    $[24] = handleItemClick;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  const renderFactiiiItem = t8;
  let t9;
  if ($[26] !== data || $[27] !== props.type || $[28] !== renderFactiiiItem || $[29] !== renderSpaceItem || $[30] !== renderUserItem) {
    t9 = () => {
      if (props.type === "user" && data && "users" in data) {
        if (data.users?.length === 0) {
          return <div className="py-4 text-center text-textSecondary">No users found</div>;
        }
        return data.users?.map((user_0, index_2) => renderUserItem(user_0, index_2));
      } else {
        if (props.type === "space" && data && "spaces" in data) {
          if (data.spaces?.length === 0) {
            return <div className="py-4 text-center text-textSecondary">No spaces found</div>;
          }
          return data.spaces?.map((space_0, index_3) => renderSpaceItem(space_0, index_3));
        } else {
          if (props.type === "factiii" && data && "factiiis" in data) {
            if (data.factiiis?.length === 0) {
              return <div className="py-4 text-center text-textSecondary">No factiiis found</div>;
            }
            return data.factiiis?.map((factiii_0, index_4) => renderFactiiiItem(factiii_0, index_4));
          }
        }
      }
      return null;
    };
    $[26] = data;
    $[27] = props.type;
    $[28] = renderFactiiiItem;
    $[29] = renderSpaceItem;
    $[30] = renderUserItem;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  const renderContent = t9;
  if (props.fullWidth) {
    let t10;
    if ($[32] !== renderContent) {
      t10 = renderContent();
      $[32] = renderContent;
      $[33] = t10;
    } else {
      t10 = $[33];
    }
    let t11;
    if ($[34] !== isError || $[35] !== isLoading) {
      t11 = <Error error={isError} loading={isLoading} />;
      $[34] = isError;
      $[35] = isLoading;
      $[36] = t11;
    } else {
      t11 = $[36];
    }
    let t12;
    if ($[37] !== t10 || $[38] !== t11) {
      t12 = <div className="absolute inset-x-0 top-full z-50 mt-1 max-h-80 space-y-1 overflow-y-auto rounded-lg border border-ds-border-standard bg-background-primary p-2 text-textPrimary shadow-lg" onMouseDown={_temp}>{t10}{t11}</div>;
      $[37] = t10;
      $[38] = t11;
      $[39] = t12;
    } else {
      t12 = $[39];
    }
    return t12;
  }
  let t10;
  if ($[40] !== props.caretPosition) {
    t10 = props.caretPosition || {
      top: 0,
      left: 0
    };
    $[40] = props.caretPosition;
    $[41] = t10;
  } else {
    t10 = $[41];
  }
  const position = t10;
  const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1000;
  const clampedLeft = Math.min(position.left, viewportWidth - 288 - 16);
  const finalLeft = Math.max(16, clampedLeft);
  let t11;
  if ($[42] !== props) {
    t11 = <div className="fixed inset-0 z-popover bg-black opacity-50" onMouseDown={e_3 => {
      e_3.preventDefault();
      props.closeHandler();
    }} />;
    $[42] = props;
    $[43] = t11;
  } else {
    t11 = $[43];
  }
  const t12 = `${position.top}px`;
  const t13 = `${finalLeft}px`;
  let t14;
  if ($[44] !== t12 || $[45] !== t13) {
    t14 = {
      top: t12,
      left: t13
    };
    $[44] = t12;
    $[45] = t13;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  let t15;
  if ($[47] !== renderContent) {
    t15 = renderContent();
    $[47] = renderContent;
    $[48] = t15;
  } else {
    t15 = $[48];
  }
  let t16;
  if ($[49] !== isError || $[50] !== isLoading) {
    t16 = <Error error={isError} loading={isLoading} />;
    $[49] = isError;
    $[50] = isLoading;
    $[51] = t16;
  } else {
    t16 = $[51];
  }
  let t17;
  if ($[52] !== t14 || $[53] !== t15 || $[54] !== t16) {
    t17 = <div className="fixed z-popover h-56 max-h-96 w-full max-w-72 space-y-1 overflow-y-auto rounded-lg bg-background-primary p-2 text-textPrimary shadow-lg" style={t14} onMouseDown={_temp2}>{t15}{t16}</div>;
    $[52] = t14;
    $[53] = t15;
    $[54] = t16;
    $[55] = t17;
  } else {
    t17 = $[55];
  }
  let t18;
  if ($[56] !== t11 || $[57] !== t17) {
    t18 = <>{t11}{t17}</>;
    $[56] = t11;
    $[57] = t17;
    $[58] = t18;
  } else {
    t18 = $[58];
  }
  return t18;
};
export default TagSuggestionModal;
function _temp(e_2) {
  e_2.preventDefault();
}
function _temp2(e_4) {
  e_4.preventDefault();
}